/**
 * RowEditor — the structured clause-row editors for the view configurator. One
 * component per clause kind (show/hide/focus, group, at, on, color, style, flow,
 * table, matrix, grid, index, raw); each renders the pickers for that kind and
 * reports a whole new ClauseRow — the text form is derived, never typed (except
 * the `raw` escape hatches). The selection clauses render `TermEditor`, one
 * recursive component over the whole selector tree. Extracted from ViewBuilder
 * so the panel shell stays a pure view; these are the shared editing widgets it
 * composes.
 */
import { useState, type ReactElement } from "react";
import { Plus, X } from "lucide-react";
import { Button, IconButton } from "@archlang/ui";
import {
  seedFlowBodyRow,
  seedStyleTarget,
  seedRefinement,
  seedSelTerm,
  selTermText,
  coerceStyleMembersForTarget,
  EDGE_DEFAULT_HEX,
  EXTRACTION_HEADS,
  FLOW_BODY_KINDS,
  type ClauseRow,
  type SelTerm,
  type SelTermKind,
  type ExtractionHead,
  type SelectorAtom,
  type StyleMemberRow,
  type StyleTarget,
  type ArrowOp,
  type Refinement,
  type RefinementKind,
  type FlowMode,
  type FlowBodyRow,
  type FlowBodyKind,
  type TableColumnRow,
  type MatrixOrder,
  type ScrubPointRow,
} from "./clauseModel.js";
import { PlaneSelect } from "../components/pickers/PlaneSelect.js";
import { ColorBySelect } from "../components/pickers/ColorBySelect.js";
import { EntityPicker } from "../components/pickers/EntityPicker.js";
import type { SelectorVocabulary } from "./useSelectorVocabulary.js";

export function RowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: ClauseRow;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement | null {
  switch (row.kind) {
    case "show":
    case "hide":
    case "focus":
      return <SelectorRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "groupBy":
      return (
        <select
          aria-label={`Clause ${index + 1} aspect key`}
          className="w-full bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-xs text-slate-300"
          value={row.key}
          onChange={(e) => onChange({ kind: "groupBy", key: e.target.value })}
        >
          <option value="">pick an aspect key…</option>
          {vocab.aspectKeys.map((k) => (
            <option key={k} value={k}>
              @@{k}
            </option>
          ))}
        </select>
      );
    case "at":
      return <AtRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "on":
      // PlaneSelect renders null with no planes (the HUD relies on that); the
      // builder host shows a hint so the row isn't a mystery empty box.
      return vocab.planes.length === 0 ? (
        <div className="text-[11px] text-slate-600 italic">this model declares no planes</div>
      ) : (
        <PlaneSelect
          planes={vocab.planes}
          value={row.plane || null}
          onChange={(p) => onChange({ kind: "on", plane: p ?? "" })}
          placeholder="pick a plane…"
          aria-label={`Clause ${index + 1} plane`}
          title="Focus the view on one plane's presence subset"
        />
      );
    case "color":
      return (
        <ColorBySelect
          aspectKeys={vocab.aspectKeys}
          value={row.key || null}
          onChange={(k) => onChange({ kind: "color", key: k ?? "" })}
          noneLabel="pick an aspect…"
          aria-label={`Clause ${index + 1} colour aspect`}
          title="Colour every module by an aspect key — style * { color: colorize(@@key) }"
        />
      );
    case "style":
      return <StyleRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "flow":
      return <FlowRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "table":
      return <TableRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "matrix":
      return <MatrixRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "grid":
      return <GridRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "index":
      return <IndexRowEditor index={index} row={row} vocab={vocab} onChange={onChange} />;
    case "raw":
      return (
        <input
          aria-label={`Clause ${index + 1} raw text`}
          className="w-full bg-slate-800 border border-slate-700 rounded-sm px-2 py-1 text-xs font-mono text-slate-200 focus:border-slate-500 focus:outline-hidden"
          placeholder="any clause — full selector algebra (style, flow, table…)"
          value={row.text}
          onChange={(e) => onChange({ kind: "raw", text: e.target.value })}
        />
      );
  }
}

/** The selection clause's editor — one recursive term tree (`SelTerm`). */
function SelectorRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { sel: SelTerm }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  return (
    <TermEditor
      ariaPrefix={`Clause ${index + 1}`}
      term={row.sel}
      vocab={vocab}
      onChange={(sel) => onChange({ ...row, sel })}
    />
  );
}

/** The term kinds the picker offers, grouped the way they read. `atom` is split
 *  across its three payload shapes so one dropdown covers "pick a thing" without
 *  a second kind selector underneath. */
const TERM_OPTIONS: readonly { readonly group: string; readonly value: string; readonly label: string }[] = [
  { group: "matches", value: "atom:type", label: "type" },
  { group: "matches", value: "atom:element", label: "element" },
  { group: "matches", value: "atom:aspect", label: "aspect" },
  { group: "matches", value: "atom:all", label: "everything (*)" },
  { group: "predicates", value: "in", label: "in <scope>" },
  { group: "predicates", value: "depth", label: "depth <N>" },
  { group: "predicates", value: "on", label: "on <plane>" },
  { group: "predicates", value: "where", label: "where (<expr>)" },
  { group: "predicates", value: "violating", label: "violating <policy>" },
  { group: "predicates", value: "outside", label: "outside the view" },
  { group: "edges", value: "edge", label: "a > b (edges)" },
  { group: "edges", value: "extract", label: "sources/targets of…" },
  { group: "groups", value: "and", label: "all of…" },
  { group: "groups", value: "or", label: "any of…" },
  { group: "groups", value: "not", label: "not…" },
  { group: "groups", value: "raw", label: "raw selector text" },
];

const TERM_GROUPS = ["matches", "predicates", "edges", "groups"] as const;

/** The dropdown value a term currently reads as. */
function termOptionValue(t: SelTerm): string {
  return t.kind === "atom" ? `atom:${t.atom.kind}` : t.kind;
}

/** Build a fresh term for a picked dropdown value, carrying nothing over — a
 *  kind switch is a deliberate replacement, and the old payload has no slot in
 *  the new shape. A group keeps the previous term as its first member, so
 *  wrapping an in-progress selector never discards it. */
function termForOption(value: string, previous: SelTerm): SelTerm {
  if (value.startsWith("atom:")) {
    const kind = value.slice(5) as SelectorAtom["kind"];
    const atom: SelectorAtom =
      kind === "all"
        ? { kind: "all" }
        : kind === "type"
          ? { kind: "type", name: "" }
          : kind === "element"
            ? { kind: "element", path: "" }
            : { kind: "aspect", key: "" };
    return { kind: "atom", atom };
  }
  const kind = value as SelTermKind;
  if (kind === "and" || kind === "or") return { kind, terms: [previous] };
  if (kind === "not") return { kind: "not", term: previous };
  return seedSelTerm(kind);
}

/**
 * One selector term + its subtree. A group (`and`/`or`) renders its members
 * indented under the joiner with an add-bar; every other kind renders its own
 * slot. The kind dropdown doubles as the wrap control — picking "all of…" on a
 * term nests THAT term as the group's first member, so composing
 * `(a or b) and not c` is three picks, not a raw-text fallback.
 */
function TermEditor({
  ariaPrefix,
  term,
  vocab,
  onChange,
  onRemove,
}: {
  ariaPrefix: string;
  term: SelTerm;
  vocab: SelectorVocabulary;
  onChange: (t: SelTerm) => void;
  onRemove?: () => void;
}): ReactElement {
  const kindSelect = (
    <select
      aria-label={`${ariaPrefix} term kind`}
      className="shrink-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
      value={termOptionValue(term)}
      onChange={(e) => onChange(termForOption(e.target.value, term))}
    >
      {TERM_GROUPS.map((g) => (
        <optgroup key={g} label={g}>
          {TERM_OPTIONS.filter((o) => o.group === g).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );

  const remove = onRemove ? (
    <IconButton size="xs" aria-label={`Remove ${ariaPrefix}`} onClick={onRemove}>
      <X size={11} />
    </IconButton>
  ) : null;

  if (term.kind === "and" || term.kind === "or") {
    const setTerms = (terms: readonly SelTerm[]): void => onChange({ ...term, terms });
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          {kindSelect}
          <span className="flex-1 truncate font-mono text-[10px] text-slate-600">
            {selTermText(term) ?? "· add a term"}
          </span>
          {remove}
        </div>
        <div className="space-y-1 border-l border-slate-800 pl-2">
          {term.terms.map((t, i) => (
            <TermEditor
              key={i}
              ariaPrefix={`${ariaPrefix} term ${i + 1}`}
              term={t}
              vocab={vocab}
              onChange={(nt) => setTerms(term.terms.map((x, j) => (j === i ? nt : x)))}
              onRemove={() => setTerms(term.terms.filter((_, j) => j !== i))}
            />
          ))}
          <Button
            variant="ghost"
            size="xs"
            aria-label={`Add a term to ${ariaPrefix}`}
            onClick={() => setTerms([...term.terms, seedSelTerm("atom")])}
          >
            <Plus size={10} /> term
          </Button>
        </div>
      </div>
    );
  }

  if (term.kind === "not") {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          {kindSelect}
          <span className="flex-1 truncate font-mono text-[10px] text-slate-600">{selTermText(term) ?? "· not what?"}</span>
          {remove}
        </div>
        <div className="border-l border-slate-800 pl-2">
          <TermEditor
            ariaPrefix={`${ariaPrefix} inner`}
            term={term.term}
            vocab={vocab}
            onChange={(t) => onChange({ kind: "not", term: t })}
          />
        </div>
      </div>
    );
  }

  if (term.kind === "extract") {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          {kindSelect}
          <select
            aria-label={`${ariaPrefix} extraction head`}
            className="shrink-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
            value={term.head}
            onChange={(e) => onChange({ ...term, head: e.target.value as ExtractionHead })}
          >
            {EXTRACTION_HEADS.map((h) => (
              <option key={h} value={h}>
                {h} of
              </option>
            ))}
          </select>
          {remove}
        </div>
        <div className="border-l border-slate-800 pl-2">
          <TermEditor
            ariaPrefix={`${ariaPrefix} inner`}
            term={term.inner}
            vocab={vocab}
            onChange={(t) => onChange({ ...term, inner: t })}
          />
        </div>
      </div>
    );
  }

  if (term.kind === "edge") {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          {kindSelect}
          <select
            aria-label={`${ariaPrefix} arrow`}
            className="shrink-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
            value={term.op}
            onChange={(e) => onChange({ ...term, op: e.target.value as ArrowOp })}
          >
            <option value=">">&gt; direct</option>
            <option value=">>">&gt;&gt; reaches</option>
            <option value="<>">&lt;&gt; either way</option>
          </select>
          {/* `within` is a hop bound only `>>`/`<>` accept — the parser
              rejects `A > B within 2` and points at `>>`. Offering the field on
              `>` invited unparseable source, so it is hidden there. */}
          {term.op !== ">" && (
            <input
              aria-label={`${ariaPrefix} within`}
              className="w-16 shrink-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] font-mono text-slate-300"
              placeholder="within"
              value={term.within}
              // A count or a `$knob`, nothing else: free text here wrote
              // `within abc` straight into the model.
              onChange={(e) =>
                onChange({ ...term, within: e.target.value.replace(/[^0-9$A-Za-z_.]/g, "") })
              }
            />
          )}
          {remove}
        </div>
        <div className="space-y-1 border-l border-slate-800 pl-2">
          <TermEditor
            ariaPrefix={`${ariaPrefix} from`}
            term={term.from}
            vocab={vocab}
            onChange={(t) => onChange({ ...term, from: t })}
          />
          <TermEditor
            ariaPrefix={`${ariaPrefix} to`}
            term={term.to}
            vocab={vocab}
            onChange={(t) => onChange({ ...term, to: t })}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {kindSelect}
      <TermSlot ariaPrefix={ariaPrefix} term={term} vocab={vocab} onChange={onChange} />
      {remove}
    </div>
  );
}

/** The payload slot of a LEAF term — everything the kind dropdown does not
 *  itself carry. `outside` and `*` have none. */
function TermSlot({
  ariaPrefix,
  term,
  vocab,
  onChange,
}: {
  ariaPrefix: string;
  term: SelTerm;
  vocab: SelectorVocabulary;
  onChange: (t: SelTerm) => void;
}): ReactElement | null {
  switch (term.kind) {
    case "atom":
      return term.atom.kind === "aspect" ? (
        <AspectAtomSlot
          ariaPrefix={ariaPrefix}
          atom={term.atom}
          vocab={vocab}
          onChange={(atom) => onChange({ kind: "atom", atom })}
        />
      ) : (
        <AtomValuePicker
          ariaPrefix={ariaPrefix}
          atom={term.atom}
          vocab={vocab}
          onChange={(atom) => onChange({ kind: "atom", atom })}
        />
      );
    case "in":
      return (
        <>
          <EntityPicker
            aria-label={`${ariaPrefix} scope`}
            mode="tree"
            className="flex-1 min-w-0"
            placeholder="pick a scope…"
            items={[...new Set([...vocab.elements, ...vocab.processes])].sort()}
            value={term.scope || null}
            onChange={(v) => onChange({ ...term, scope: v ?? "" })}
          />
          {/* `depth N` clips the descent to N levels below the scope; blank =
              unbounded, which is the plain `in <scope>` this has always been. */}
          <DepthInput
            ariaLabel={`${ariaPrefix} depth`}
            value={term.depth ?? ""}
            onChange={(depth) => onChange({ ...term, depth })}
          />
        </>
      );
    case "depth":
      return (
        <DepthInput
          ariaLabel={`${ariaPrefix} depth`}
          value={term.depth}
          onChange={(depth) => onChange({ kind: "depth", depth })}
        />
      );
    case "on":
      return (
        <select
          aria-label={`${ariaPrefix} plane`}
          className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
          value={term.plane}
          onChange={(e) => onChange({ kind: "on", plane: e.target.value })}
        >
          <option value="">pick a plane…</option>
          {vocab.planes.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      );
    case "where":
      return (
        <input
          aria-label={`${ariaPrefix} where expression`}
          className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] font-mono text-slate-300"
          placeholder="expression, e.g. @connections > 20"
          value={term.expr}
          onChange={(e) => onChange({ kind: "where", expr: e.target.value })}
        />
      );
    case "violating":
      return (
        <select
          aria-label={`${ariaPrefix} policy`}
          className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
          value={term.policy}
          onChange={(e) => onChange({ kind: "violating", policy: e.target.value })}
        >
          <option value="">pick a policy…</option>
          {vocab.policies.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      );
    case "raw":
      return (
        <input
          aria-label={`${ariaPrefix} raw selector`}
          className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] font-mono text-slate-300"
          placeholder="any selector fragment"
          value={term.text}
          onChange={(e) => onChange({ kind: "raw", text: e.target.value })}
        />
      );
    default:
      return null;
  }
}

/** An aspect atom's key + optional value — the one atom shape with two slots. */
/** The `depth N` bound — a count or a `$knob`, the same accepted alphabet the
 *  arrow's `within` field uses. Blank = unbounded (the term stays legal, it just
 *  emits no suffix), so the field can sit permanently beside an `in` scope. */
function DepthInput({
  ariaLabel,
  value,
  onChange,
}: {
  ariaLabel: string;
  value: string;
  onChange: (v: string) => void;
}): ReactElement {
  return (
    <input
      aria-label={ariaLabel}
      className="w-16 shrink-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] font-mono text-slate-300"
      placeholder="depth"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9$A-Za-z_.]/g, ""))}
    />
  );
}

function AspectAtomSlot({
  ariaPrefix,
  atom,
  vocab,
  onChange,
}: {
  ariaPrefix: string;
  atom: Extract<SelectorAtom, { kind: "aspect" }>;
  vocab: SelectorVocabulary;
  onChange: (a: SelectorAtom) => void;
}): ReactElement {
  return (
    <>
      <EntityPicker
        aria-label={`${ariaPrefix} aspect key`}
        mode="list"
        className="flex-1 min-w-0"
        placeholder="pick a key…"
        items={vocab.aspectKeys}
        value={atom.key || null}
        onChange={(v) => onChange({ kind: "aspect", key: v ?? "" })}
      />
      <select
        aria-label={`${ariaPrefix} aspect value`}
        className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
        value={atom.value ?? ""}
        onChange={(e) => onChange({ kind: "aspect", key: atom.key, ...(e.target.value ? { value: e.target.value } : {}) })}
        disabled={!atom.key}
      >
        <option value="">any value</option>
        {(atom.key ? vocab.aspectValues(atom.key) : []).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    </>
  );
}

/** The `at <point>[..<point>]` editor — the view's timeline reading point, and
 *  optionally the second endpoint that turns it into the roadmap delta. Each
 *  point is `now` or a marker; the marker list is what the model itself writes
 *  (`vocab.markers`), with a free-text slot for one it does not — a marker needs
 *  no declaration, it resolves to its place BETWEEN ticks (spec §9.1). */
function AtRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { kind: "at" }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  const point = (
    label: string,
    value: ScrubPointRow,
    set: (p: ScrubPointRow | null) => void,
    clearable: boolean,
  ): ReactElement => {
    const custom = value.kind === "marker" && !vocab.markers.includes(value.text);
    return (
      <div className="flex items-center gap-1">
        <span className="w-16 shrink-0 text-[10px] uppercase text-slate-600">{label}</span>
        <select
          aria-label={`Clause ${index + 1} ${label} point`}
          className="bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
          value={value.kind === "now" ? " now" : custom ? " custom" : value.text}
          onChange={(e) => {
            const v = e.target.value;
            if (v === " now") set({ kind: "now" });
            else if (v === " custom") set({ kind: "marker", text: "" });
            else set({ kind: "marker", text: v });
          }}
        >
          <option value=" now">now</option>
          {vocab.markers.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
          <option value=" custom">a marker not on the axis…</option>
        </select>
        {custom && (
          <input
            aria-label={`Clause ${index + 1} ${label} marker`}
            className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] font-mono text-slate-300"
            placeholder={'v3 — quote one that is not one token: "2026-Q3"'}
            value={value.text}
            onChange={(e) => set({ kind: "marker", text: e.target.value })}
          />
        )}
        {clearable && (
          <IconButton size="xs" aria-label={`Remove the compare point from clause ${index + 1}`} onClick={() => set(null)}>
            <X size={11} />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-1">
      {point("at", row.at, (p) => p && onChange({ ...row, at: p }), false)}
      {row.compare ? (
        point(
          "compare",
          row.compare,
          (p) => onChange(p ? { ...row, compare: p } : { kind: "at", at: row.at }),
          true,
        )
      ) : (
        <Button
          variant="ghost"
          size="xs"
          aria-label={`Add a compare point to clause ${index + 1}`}
          onClick={() => onChange({ ...row, compare: { kind: "now" } })}
        >
          <Plus size={10} /> compare to…
        </Button>
      )}
    </div>
  );
}

// The refinement kinds the add-bar offers (the SELECTORS §4 `and`-predicates).
const REFINEMENT_ADDS: readonly { readonly label: string; readonly kind: RefinementKind }[] = [
  { label: "and", kind: "andAtom" },
  { label: "and not", kind: "notAtom" },
  { label: "in", kind: "in" },
  { label: "where", kind: "where" },
];

/** The shared `and`-refinement list editor — a row per refinement (`and <atom>` /
 *  `and not <atom>` / `in <scope>` / `where (<expr>)`) + an add-bar. Narrows a base
 *  set; used by the show/hide/focus selectors and the style node target. */
function RefinementList({
  ariaPrefix,
  refinements,
  vocab,
  onChange,
}: {
  ariaPrefix: string;
  refinements: readonly Refinement[];
  vocab: SelectorVocabulary;
  onChange: (refinements: readonly Refinement[]) => void;
}): ReactElement {
  return (
    <div className="space-y-1">
      {refinements.map((r, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="min-w-0 flex-1">
            <RefinementEditor
              ariaPrefix={`${ariaPrefix} refinement ${i + 1}`}
              refinement={r}
              vocab={vocab}
              onChange={(nr) => onChange(refinements.map((x, j) => (j === i ? nr : x)))}
            />
          </div>
          <IconButton
            size="xs"
            aria-label={`Remove refinement ${i + 1} from ${ariaPrefix}`}
            onClick={() => onChange(refinements.filter((_, j) => j !== i))}
          >
            <X size={11} />
          </IconButton>
        </div>
      ))}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {REFINEMENT_ADDS.map((a) => (
          <Button
            key={a.kind}
            variant="ghost"
            size="xs"
            aria-label={`Add ${a.label} to ${ariaPrefix}`}
            onClick={() => onChange([...refinements, seedRefinement(a.kind)])}
          >
            <Plus size={10} /> {a.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** One refinement's editor: an atom picker (`and` / `and not`), a scope tree
 *  (`in`, over elements + processes), or a raw expression input (`where`). */
function RefinementEditor({
  ariaPrefix,
  refinement,
  vocab,
  onChange,
}: {
  ariaPrefix: string;
  refinement: Refinement;
  vocab: SelectorVocabulary;
  onChange: (r: Refinement) => void;
}): ReactElement {
  const slot = (t: string): ReactElement => <span className="shrink-0 text-[10px] uppercase text-slate-600">{t}</span>;
  switch (refinement.kind) {
    case "andAtom":
    case "notAtom":
      return (
        <div className="flex items-center gap-1">
          {slot(refinement.kind === "notAtom" ? "and not" : "and")}
          <AtomSelect ariaPrefix={ariaPrefix} atom={refinement.atom} vocab={vocab} onChange={(atom) => onChange({ kind: refinement.kind, atom })} />
        </div>
      );
    case "in":
      return (
        <div className="flex items-center gap-1">
          {slot("in")}
          <EntityPicker
            aria-label={`${ariaPrefix} scope`}
            mode="tree"
            className="flex-1 min-w-0"
            placeholder="pick a scope…"
            items={[...new Set([...vocab.elements, ...vocab.processes])].sort()}
            value={refinement.scope || null}
            onChange={(v) => onChange({ kind: "in", scope: v ?? "" })}
          />
        </div>
      );
    case "where":
      return (
        <div className="flex items-center gap-1">
          {slot("where")}
          <input
            aria-label={`${ariaPrefix} where expression`}
            className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] font-mono text-slate-300"
            placeholder="expression, e.g. @connections > 20"
            value={refinement.expr}
            onChange={(e) => onChange({ kind: "where", expr: e.target.value })}
          />
        </div>
      );
  }
}

/** The searchable value picker for one atom's payload — a type / aspect-key
 *  filter list or an element tree (`all` has no payload). Shared by the
 *  single-atom selectors (style target, flow lane/pool). */
function AtomValuePicker({
  ariaPrefix,
  atom,
  vocab,
  onChange,
}: {
  ariaPrefix: string;
  atom: SelectorAtom;
  vocab: SelectorVocabulary;
  onChange: (atom: SelectorAtom) => void;
}): ReactElement | null {
  if (atom.kind === "type")
    return (
      <EntityPicker
        aria-label={`${ariaPrefix} type`}
        mode="list"
        className="flex-1 min-w-0"
        placeholder="pick a type…"
        items={vocab.types}
        value={atom.name || null}
        onChange={(v) => onChange({ kind: "type", name: v ?? "" })}
      />
    );
  if (atom.kind === "element")
    return (
      <EntityPicker
        aria-label={`${ariaPrefix} element`}
        mode="tree"
        className="flex-1 min-w-0"
        placeholder="pick an element…"
        items={vocab.elements}
        value={atom.path || null}
        onChange={(v) => onChange({ kind: "element", path: v ?? "" })}
      />
    );
  if (atom.kind === "aspect")
    return (
      <EntityPicker
        aria-label={`${ariaPrefix} aspect key`}
        mode="list"
        className="flex-1 min-w-0"
        placeholder="pick a key…"
        items={vocab.aspectKeys}
        value={atom.key || null}
        onChange={(v) => onChange({ kind: "aspect", key: v ?? "", ...(atom.value ? { value: atom.value } : {}) })}
      />
    );
  return null;
}

type MemberAdd = { readonly label: string; readonly seed: () => StyleMemberRow };

// The NODE style-member kinds the add-bar offers, each with a seed. `pin` is absent
// — it's drag-authored (shown read-only when present), never added here.
const STYLE_MEMBER_ADDS: readonly MemberAdd[] = [
  { label: "colour", seed: () => ({ kind: "color", value: { kind: "colorize", key: "" } }) },
  { label: "icon", seed: () => ({ kind: "widget", prop: "icon", value: { kind: "ident", text: "" } }) },
  { label: "badge", seed: () => ({ kind: "widget", prop: "badge", value: { kind: "string", text: "" } }) },
  { label: "subheader", seed: () => ({ kind: "widget", prop: "subheader", value: { kind: "string", text: "" } }) },
  { label: "rename", seed: () => ({ kind: "rename", label: "" }) },
  { label: "use bundle", seed: () => ({ kind: "use", bundle: "" }) },
];

// An EDGE/POLICY target paints edges — only colour (hex) + width (VIEWS §5; engine
// `EdgeStyle` = color + width). Colour seeds hex, never a cast (a cast paints nothing).
const EDGE_MEMBER_ADDS: readonly MemberAdd[] = [
  { label: "colour", seed: () => ({ kind: "color", value: { kind: "hex", value: EDGE_DEFAULT_HEX } }) },
  { label: "width", seed: () => ({ kind: "width", px: 2 }) },
];

/** `style <target> { <members> }` — a target (node atom / edge `A > B` / policy
 *  `violating <P>`) + an ordered member list. A NODE target offers colour casts,
 *  widget.icon/badge/subheader, rename, use-bundle; an EDGE/POLICY target paints
 *  edges, so it offers only colour (hex) + width. Switching mode re-seeds the target
 *  and coerces the members to the new set (§3.6). `pin` is drag-authored (read-only
 *  chip). Anything the rows can't hold (computed exprs, `css()`, compound edge sides)
 *  stays in the `raw` escape hatch. */
function StyleRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { kind: "style" }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  const target = row.selector;
  const isEdgeLike = target.kind !== "node";
  const adds = isEdgeLike ? EDGE_MEMBER_ADDS : STYLE_MEMBER_ADDS;
  const setTarget = (selector: StyleTarget): void => onChange({ ...row, selector });
  const setMembers = (members: readonly StyleMemberRow[]): void => onChange({ ...row, members });
  // Mode switch re-seeds the target AND coerces the body to what it can paint
  // (edge/policy ⇒ hex colour + width; drops node-only members). §3.6 hex-lock.
  const setMode = (kind: StyleTarget["kind"]): void =>
    onChange({ ...row, selector: seedStyleTarget(kind), members: coerceStyleMembersForTarget(row.members, kind) });

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        <select
          aria-label={`Clause ${index + 1} style target mode`}
          className="bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
          value={target.kind}
          onChange={(e) => setMode(e.target.value as StyleTarget["kind"])}
        >
          <option value="node">node</option>
          <option value="edge">edge</option>
          <option value="policy">policy</option>
        </select>
        <StyleTargetSlots index={index} target={target} vocab={vocab} onChange={setTarget} />
      </div>
      {target.kind === "node" && (
        <div className="pl-2">
          <RefinementList
            ariaPrefix={`clause ${index + 1} style`}
            refinements={target.refinements ?? []}
            vocab={vocab}
            onChange={(refinements) => setTarget({ ...target, refinements })}
          />
        </div>
      )}
      {row.members.map((m, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="min-w-0 flex-1">
            <StyleMemberEditor
              index={index}
              member={m}
              vocab={vocab}
              colorHexOnly={isEdgeLike}
              onChange={(nm) => setMembers(row.members.map((x, j) => (j === i ? nm : x)))}
            />
          </div>
          <IconButton
            size="xs"
            aria-label={`Remove style member ${i + 1} of clause ${index + 1}`}
            onClick={() => setMembers(row.members.filter((_, j) => j !== i))}
          >
            <X size={11} />
          </IconButton>
        </div>
      ))}
      <div className="flex flex-wrap gap-x-3 gap-y-1 pl-10">
        {adds.map((a) => (
          <Button
            key={a.label}
            variant="ghost"
            size="xs"
            aria-label={`Add ${a.label} to clause ${index + 1}`}
            onClick={() => setMembers([...row.members, a.seed()])}
          >
            <Plus size={10} /> {a.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** The target slot pickers for a style rule: a node atom, an edge `from op to`, or
 *  a policy dropdown. `node`/edge sides reuse `AtomSelect` (kind + value + aspect
 *  value); the op select ships `>` only (the sole proven edge-set op — §3.1). */
function StyleTargetSlots({
  index,
  target,
  vocab,
  onChange,
}: {
  index: number;
  target: StyleTarget;
  vocab: SelectorVocabulary;
  onChange: (target: StyleTarget) => void;
}): ReactElement {
  const base = `Clause ${index + 1} style`;
  if (target.kind === "node")
    return <AtomSelect ariaPrefix={base} atom={target.atom} vocab={vocab} onChange={(atom) => onChange({ kind: "node", atom })} />;
  if (target.kind === "edge") {
    const slotLabel = (t: string): ReactElement => (
      <span className="w-8 shrink-0 text-[10px] uppercase text-slate-600">{t}</span>
    );
    // Stacked from / op / to rows — three edge slots inline would squish the
    // element pickers; a full-width block keeps each side readable.
    return (
      <div className="w-full mt-1 pl-2 border-l border-slate-800 space-y-1">
        <div className="flex items-center gap-1">
          {slotLabel("from")}
          <AtomSelect ariaPrefix={`${base} from`} atom={target.from} vocab={vocab} onChange={(from) => onChange({ ...target, from })} />
        </div>
        <div className="flex items-center gap-1">
          {slotLabel("op")}
          <select
            aria-label={`${base} op`}
            className="bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
            value={target.op}
            onChange={(e) => onChange({ ...target, op: e.target.value as ArrowOp })}
          >
            <option value=">">&gt; depends on</option>
          </select>
        </div>
        <div className="flex items-center gap-1">
          {slotLabel("to")}
          <AtomSelect ariaPrefix={`${base} to`} atom={target.to} vocab={vocab} onChange={(to) => onChange({ ...target, to })} />
        </div>
      </div>
    );
  }
  return vocab.policies.length === 0 ? (
    <span className="text-[11px] text-slate-600 italic">no policies declared</span>
  ) : (
    <select
      aria-label={`${base} policy`}
      className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
      value={target.policy}
      onChange={(e) => onChange({ kind: "policy", policy: e.target.value })}
    >
      <option value="">pick a policy…</option>
      {vocab.policies.map((p) => (
        <option key={p} value={p}>
          {p}
        </option>
      ))}
    </select>
  );
}

/** The editor for one style-body member: a colour cast, a widget leaf (icon /
 *  badge / subheader), rename, use-bundle, or a read-only pin chip. Widget values
 *  keep the token kind — icon is an identifier (icon picker or getter), badge /
 *  subheader are strings (free text or getter) — so re-emit never corrupts. */
function StyleMemberEditor({
  index,
  member,
  vocab,
  colorHexOnly,
  onChange,
}: {
  index: number;
  member: StyleMemberRow;
  vocab: SelectorVocabulary;
  /** Edge/policy target — colour is a hex swatch only (casts don't paint an edge). */
  colorHexOnly?: boolean;
  onChange: (m: StyleMemberRow) => void;
}): ReactElement {
  const selectCls = "bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  const fieldCls = "flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  const slotLabel = (t: string): ReactElement => (
    <span className="w-14 shrink-0 text-[10px] uppercase text-slate-600">{t}</span>
  );
  const getters = gettersOf(vocab);

  switch (member.kind) {
    case "color": {
      const v = member.value;
      return (
        <div className="flex items-center gap-1">
          {slotLabel("colour")}
          {!colorHexOnly && (
            <select
              aria-label={`Clause ${index + 1} colour mode`}
              className={selectCls}
              value={v.kind}
              onChange={(e) =>
                onChange({
                  kind: "color",
                  value:
                    e.target.value === "colorize"
                      ? { kind: "colorize", key: v.kind !== "hex" ? v.key : "" }
                      : e.target.value === "heat"
                        ? { kind: "heat", key: v.kind !== "hex" ? v.key : "" }
                        : { kind: "hex", value: v.kind === "hex" ? v.value : "#f43f5e" },
                })
              }
            >
              <option value="colorize">by aspect</option>
              <option value="heat">heat</option>
              <option value="hex">flat</option>
            </select>
          )}
          {v.kind === "hex" ? (
            <label className="inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-300">
              <input
                type="color"
                aria-label={`Clause ${index + 1} flat colour`}
                className="h-6 w-8 rounded-sm bg-transparent border border-slate-700 cursor-pointer"
                value={v.value || "#f43f5e"}
                onChange={(e) => onChange({ kind: "color", value: { kind: "hex", value: e.target.value } })}
              />
              {v.value || "#f43f5e"}
            </label>
          ) : (
            <ColorBySelect
              aspectKeys={vocab.aspectKeys}
              value={v.key || null}
              onChange={(k) =>
                onChange({
                  kind: "color",
                  value: v.kind === "heat" ? { kind: "heat", key: k ?? "" } : { kind: "colorize", key: k ?? "" },
                })
              }
              noneLabel={v.kind === "heat" ? "pick a numeric aspect…" : "pick an aspect…"}
              aria-label={`Clause ${index + 1} colour aspect`}
            />
          )}
        </div>
      );
    }
    case "widget": {
      // icon = identifier (icon picker | getter); badge / subheader = string (text | getter).
      const isIcon = member.prop === "icon";
      const v = member.value;
      return (
        <div className="flex items-center gap-1">
          {slotLabel(member.prop)}
          <select
            aria-label={`Clause ${index + 1} ${member.prop} mode`}
            className={selectCls}
            value={v.kind === "getter" ? "getter" : "value"}
            onChange={(e) =>
              onChange({
                kind: "widget",
                prop: member.prop,
                value:
                  e.target.value === "getter"
                    ? { kind: "getter", getter: "" }
                    : isIcon
                      ? { kind: "ident", text: "" }
                      : { kind: "string", text: "" },
              })
            }
          >
            <option value="value">{isIcon ? "pick" : "text"}</option>
            <option value="getter">getter</option>
          </select>
          {v.kind === "getter" ? (
            <select
              aria-label={`Clause ${index + 1} ${member.prop} getter`}
              className={fieldCls}
              value={v.getter}
              onChange={(e) => onChange({ kind: "widget", prop: member.prop, value: { kind: "getter", getter: e.target.value } })}
            >
              <option value="">pick a getter…</option>
              {getters.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          ) : isIcon ? (
            <EntityPicker
              aria-label={`Clause ${index + 1} icon`}
              mode="list"
              className="flex-1 min-w-0"
              placeholder="pick an icon…"
              items={vocab.icons}
              value={v.kind === "ident" ? v.text || null : null}
              onChange={(ic) => onChange({ kind: "widget", prop: "icon", value: { kind: "ident", text: ic ?? "" } })}
            />
          ) : (
            <input
              aria-label={`Clause ${index + 1} ${member.prop} text`}
              className={fieldCls}
              placeholder={member.prop}
              value={v.kind === "string" ? v.text : ""}
              onChange={(e) => onChange({ kind: "widget", prop: member.prop, value: { kind: "string", text: e.target.value } })}
            />
          )}
        </div>
      );
    }
    case "rename":
      return (
        <div className="flex items-center gap-1">
          {slotLabel("rename")}
          <input
            aria-label={`Clause ${index + 1} rename label`}
            className={fieldCls}
            placeholder="Display label"
            value={member.label}
            onChange={(e) => onChange({ kind: "rename", label: e.target.value })}
          />
        </div>
      );
    case "use":
      return (
        <div className="flex items-center gap-1">
          {slotLabel("use")}
          <input
            aria-label={`Clause ${index + 1} use bundle`}
            className={fieldCls}
            placeholder="Bundle name"
            value={member.bundle}
            onChange={(e) => onChange({ kind: "use", bundle: e.target.value })}
          />
        </div>
      );
    case "width":
      return (
        <div className="flex items-center gap-1">
          {slotLabel("width")}
          <input
            type="number"
            min={1}
            step={1}
            aria-label={`Clause ${index + 1} edge width`}
            className="w-16 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
            value={member.px}
            onChange={(e) => onChange({ kind: "width", px: Math.max(1, Math.round(Number(e.target.value) || 1)) })}
          />
          <span className="text-[10px] text-slate-600">px</span>
        </div>
      );
    case "pin":
      return (
        <div className="flex items-center gap-1">
          {slotLabel("pinned")}
          <span className="text-[11px] font-mono text-slate-500">
            {member.x}, {member.y}
            <span className="ml-1 text-slate-600">(drag to move)</span>
          </span>
        </div>
      );
  }
}

// Body-row kind labels — the picker's human spellings for the six lane/pool
// clause forms (the model keys stay dashed for the switch dispatch).
const FLOW_BODY_LABELS: Record<FlowBodyKind, string> = {
  "lane-expand": "lane",
  "lane-merge": "lane merged",
  "lane-by": "lane by",
  "pool-ref": "pool",
  "pool-merge": "pool merged",
  "pool-by": "pool by",
};

/** `flow <Process> [plain|sequence|bpmn] { <lane/pool>… }` — pick a process +
 *  render mode; bpmn mode reveals the lane/pool body-row editor. */
function FlowRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { kind: "flow" }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  const selectCls =
    "flex-1 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  const setBody = (body: readonly FlowBodyRow[]): void => onChange({ ...row, body });
  const setBodyRow = (i: number, b: FlowBodyRow): void =>
    setBody(row.body.map((x, j) => (j === i ? b : x)));

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-600 w-10 shrink-0">flow</span>
        <select
          aria-label={`Clause ${index + 1} flow process`}
          className={selectCls}
          value={row.ref}
          onChange={(e) => onChange({ ...row, ref: e.target.value })}
        >
          <option value="">pick a process…</option>
          {vocab.processes.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          aria-label={`Clause ${index + 1} flow mode`}
          className="bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
          value={row.mode}
          onChange={(e) => onChange({ ...row, mode: e.target.value as FlowMode })}
        >
          <option value="plain">plain</option>
          <option value="sequence">sequence</option>
          <option value="bpmn">bpmn</option>
        </select>
      </div>
      {row.mode === "bpmn" && (
        <div className="space-y-1 pl-2 border-l border-slate-800">
          {row.body.map((b, bi) => (
            <div key={bi} className="flex items-center gap-1">
              <select
                aria-label={`Clause ${index + 1} lane/pool ${bi + 1} kind`}
                className="bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
                value={b.kind}
                onChange={(e) => setBodyRow(bi, seedFlowBodyRow(e.target.value as FlowBodyKind))}
              >
                {FLOW_BODY_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {FLOW_BODY_LABELS[k]}
                  </option>
                ))}
              </select>
              <FlowBodySlots
                ariaPrefix={`Clause ${index + 1} lane/pool ${bi + 1}`}
                body={b}
                vocab={vocab}
                onChange={(nb) => setBodyRow(bi, nb)}
              />
              <IconButton
                size="xs"
                aria-label={`Remove lane/pool ${bi + 1} from clause ${index + 1}`}
                onClick={() => setBody(row.body.filter((_, j) => j !== bi))}
              >
                <X size={11} />
              </IconButton>
            </div>
          ))}
          <Button
            variant="ghost"
            size="xs"
            aria-label={`Add lane/pool to clause ${index + 1}`}
            onClick={() => setBody([...row.body, seedFlowBodyRow("lane-expand")])}
          >
            <Plus size={11} /> lane / pool
          </Button>
        </div>
      )}
    </div>
  );
}

/** The per-kind extra slots of one lane/pool body row: a title input, a
 *  selector atom, an aspect-key getter, or an element ref. */
function FlowBodySlots({
  ariaPrefix,
  body,
  vocab,
  onChange,
}: {
  ariaPrefix: string;
  body: FlowBodyRow;
  vocab: SelectorVocabulary;
  onChange: (body: FlowBodyRow) => void;
}): ReactElement {
  const fieldCls =
    "flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  switch (body.kind) {
    case "lane-expand":
      return (
        <AtomSelect
          ariaPrefix={ariaPrefix}
          atom={body.selector}
          vocab={vocab}
          onChange={(selector) => onChange({ ...body, selector })}
        />
      );
    case "lane-merge":
    case "pool-merge":
      return (
        <>
          <input
            aria-label={`${ariaPrefix} title`}
            className={fieldCls}
            placeholder="Title"
            value={body.title}
            onChange={(e) => onChange({ ...body, title: e.target.value })}
          />
          <AtomSelect
            ariaPrefix={ariaPrefix}
            atom={body.selector}
            vocab={vocab}
            onChange={(selector) => onChange({ ...body, selector })}
          />
        </>
      );
    case "lane-by":
    case "pool-by":
      return (
        <select
          aria-label={`${ariaPrefix} aspect key`}
          className={fieldCls}
          value={body.key}
          onChange={(e) => onChange({ ...body, key: e.target.value })}
        >
          <option value="">pick an aspect…</option>
          {vocab.aspectKeys.map((k) => (
            <option key={k} value={k}>
              @@{k}
            </option>
          ))}
        </select>
      );
    case "pool-ref":
      return (
        <select
          aria-label={`${ariaPrefix} element`}
          className={fieldCls}
          value={body.ref}
          onChange={(e) => onChange({ ...body, ref: e.target.value })}
        >
          <option value="">pick an element…</option>
          {vocab.elements.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      );
  }
}

/** A single-atom selector picker (kind + nested pick, aspect values included) —
 *  the shared body of the flow-body lane/pool selector slots. */
function AtomSelect({
  ariaPrefix,
  atom,
  vocab,
  onChange,
}: {
  ariaPrefix: string;
  atom: SelectorAtom;
  vocab: SelectorVocabulary;
  onChange: (atom: SelectorAtom) => void;
}): ReactElement {
  const selectCls =
    "flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  return (
    <>
      <select
        aria-label={`${ariaPrefix} target kind`}
        className="bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300"
        value={atom.kind}
        onChange={(e) => {
          const k = e.target.value as SelectorAtom["kind"];
          onChange(
            k === "all"
              ? { kind: "all" }
              : k === "type"
                ? { kind: "type", name: "" }
                : k === "element"
                  ? { kind: "element", path: "" }
                  : { kind: "aspect", key: "" },
          );
        }}
      >
        <option value="all">everything</option>
        <option value="type">type</option>
        <option value="element">element</option>
        <option value="aspect">aspect</option>
      </select>
      {atom.kind !== "all" && (
        <AtomValuePicker ariaPrefix={`${ariaPrefix} target`} atom={atom} vocab={vocab} onChange={onChange} />
      )}
      {atom.kind === "aspect" && (
        <>
          <select
            aria-label={`${ariaPrefix} target aspect value`}
            className={selectCls}
            value={atom.value ?? ""}
            onChange={(e) =>
              onChange({ kind: "aspect", key: atom.key, ...(e.target.value ? { value: e.target.value } : {}) })
            }
            disabled={!atom.key}
          >
            <option value="">any value</option>
            {(atom.key ? vocab.aspectValues(atom.key) : []).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </>
      )}
    </>
  );
}

// The getter vocabulary for representation columns/axes — `@name` plus every
// aspect key as `@@key`. The written getter text is what lands in the clause.
function gettersOf(vocab: SelectorVocabulary): readonly string[] {
  return ["@name", ...vocab.aspectKeys.map((k) => `@@${k}`)];
}

/** `table { column <getter> ["Title"]; sort by <getter> }` — a getter-column
 *  list + an optional sort. Calculated `"Title": expr` columns stay `raw`. */
function TableRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { kind: "table" }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  const getters = gettersOf(vocab);
  const fieldCls = "bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  const setCols = (columns: readonly TableColumnRow[]): void => onChange({ ...row, columns });
  const setCol = (i: number, c: TableColumnRow): void => setCols(row.columns.map((x, j) => (j === i ? c : x)));
  return (
    <div className="space-y-1">
      {row.columns.map((c, ci) => (
        <div key={ci} className="flex items-center gap-1">
          <span className="text-[10px] uppercase text-slate-600 w-12 shrink-0">column</span>
          <select
            aria-label={`Clause ${index + 1} column ${ci + 1} getter`}
            className={`flex-1 min-w-0 ${fieldCls}`}
            value={c.getter}
            onChange={(e) => setCol(ci, { ...c, getter: e.target.value })}
          >
            <option value="">pick a getter…</option>
            {getters.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
          <input
            aria-label={`Clause ${index + 1} column ${ci + 1} title`}
            className={`flex-1 min-w-0 ${fieldCls}`}
            placeholder="Title (optional)"
            value={c.title}
            onChange={(e) => setCol(ci, { ...c, title: e.target.value })}
          />
          <IconButton
            size="xs"
            aria-label={`Remove column ${ci + 1} from clause ${index + 1}`}
            onClick={() => setCols(row.columns.filter((_, j) => j !== ci))}
          >
            <X size={11} />
          </IconButton>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="xs"
          aria-label={`Add column to clause ${index + 1}`}
          onClick={() => setCols([...row.columns, { getter: "", title: "" }])}
        >
          <Plus size={11} /> column
        </Button>
        <span className="flex-1" />
        <span className="text-[10px] uppercase text-slate-600">sort by</span>
        <select
          aria-label={`Clause ${index + 1} sort getter`}
          className={fieldCls}
          value={row.sortBy}
          onChange={(e) => onChange({ ...row, sortBy: e.target.value })}
        >
          <option value="">none</option>
          {row.columns
            .map((c) => c.getter)
            .filter((g) => g.trim())
            .map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
}

/** `matrix { [axis @@key]; [cluster | order by layer | order by @@key] }` — the
 *  square DSM: an optional grouped axis + one ordering strategy. */
function MatrixRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { kind: "matrix" }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  const fieldCls = "flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-600 w-10 shrink-0">axis</span>
        <select
          aria-label={`Clause ${index + 1} matrix axis`}
          className={fieldCls}
          value={row.axis}
          onChange={(e) => onChange({ ...row, axis: e.target.value })}
        >
          <option value="">modules (default)</option>
          {vocab.aspectKeys.map((k) => (
            <option key={k} value={k}>
              @@{k}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-600 w-10 shrink-0">order</span>
        <select
          aria-label={`Clause ${index + 1} matrix order`}
          className={fieldCls}
          value={row.order}
          onChange={(e) => onChange({ ...row, order: e.target.value as MatrixOrder })}
        >
          <option value="cluster">cluster (cycles)</option>
          <option value="layer">layer (DAG)</option>
          <option value="aspect">by aspect…</option>
          <option value="none">key order</option>
        </select>
        {row.order === "aspect" && (
          <select
            aria-label={`Clause ${index + 1} matrix order aspect`}
            className={fieldCls}
            value={row.orderKey}
            onChange={(e) => onChange({ ...row, orderKey: e.target.value })}
          >
            <option value="">pick an aspect…</option>
            {vocab.aspectKeys.map((k) => (
              <option key={k} value={k}>
                @@{k}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

/** `grid { rows @@key; cols @@key; color @@key }` — the co-membership cross-tab:
 *  row/col group getters + an optional colour getter. */
function GridRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { kind: "grid" }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  const fieldCls = "flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  const aspectSelect = (
    label: string,
    slot: "rows" | "cols" | "color",
    noneLabel: string,
  ): ReactElement => (
    <div className="flex items-center gap-1">
      <span className="text-[10px] uppercase text-slate-600 w-10 shrink-0">{label}</span>
      <select
        aria-label={`Clause ${index + 1} grid ${slot}`}
        className={fieldCls}
        value={row[slot]}
        onChange={(e) => onChange({ ...row, [slot]: e.target.value })}
      >
        <option value="">{noneLabel}</option>
        {vocab.aspectKeys.map((k) => (
          <option key={k} value={k}>
            @@{k}
          </option>
        ))}
      </select>
    </div>
  );
  return (
    <div className="space-y-1">
      {aspectSelect("rows", "rows", "modules (default)")}
      {aspectSelect("cols", "cols", "modules (default)")}
      {aspectSelect("colour", "color", "none")}
    </div>
  );
}

function IndexRowEditor({
  index,
  row,
  vocab,
  onChange,
}: {
  index: number;
  row: Extract<ClauseRow, { kind: "index" }>;
  vocab: SelectorVocabulary;
  onChange: (row: ClauseRow) => void;
}): ReactElement {
  const getters = gettersOf(vocab);
  const fieldCls = "flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded-sm px-1 py-0.5 text-[11px] text-slate-300";
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-600 w-12 shrink-0">sort by</span>
        <select
          aria-label={`Clause ${index + 1} index sort getter`}
          className={fieldCls}
          value={row.sortBy}
          onChange={(e) => onChange({ ...row, sortBy: e.target.value })}
        >
          <option value="">name (default)</option>
          {getters.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-600 w-12 shrink-0">columns</span>
        <input
          type="number"
          min={1}
          aria-label={`Clause ${index + 1} index columns`}
          className={fieldCls}
          placeholder="auto-fit"
          value={row.columns}
          onChange={(e) => onChange({ ...row, columns: e.target.value })}
        />
      </div>
    </div>
  );
}
